import { useState } from "react";
import axios from "axios";
import { Lock, Loader2, Inbox } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { "X-Admin-Key": key },
      });
      setRows(res.data);
      setAuthed(true);
    } catch (err) {
      setError("Invalid admin key.");
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl border border-border bg-white p-8"
        >
          <Logo />
          <div className="mt-8 flex items-center gap-2 font-heading text-lg font-bold text-forest">
            <Lock size={18} /> Admin Access
          </div>
          <p className="mt-2 font-body text-sm text-muted-foreground">
            Enter the admin key to view contact submissions.
          </p>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            className="mt-5 w-full rounded-lg border border-input px-4 py-3 font-body text-sm text-forest outline-none focus:border-forest"
          />
          {error && <p className="mt-2 font-body text-sm text-red-600" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-6 py-3 font-body text-sm font-semibold text-white transition-colors hover:bg-forest-600 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Access Dashboard
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <div className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo />
          <span className="font-body text-sm font-medium text-muted-foreground">Submissions</span>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex items-center gap-2 font-heading text-2xl font-extrabold text-forest">
          <Inbox size={22} /> Contact Submissions ({rows.length})
        </div>
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-border bg-white p-10 text-center font-body text-muted-foreground" data-testid="admin-empty">
            No submissions yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-submissions-list">
            {rows.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border bg-white p-6" data-testid="admin-submission-row">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-heading text-base font-bold text-forest">{r.name}</div>
                    <div className="font-body text-sm text-muted-foreground">{r.organization}</div>
                  </div>
                  <div className="text-right">
                    <a href={`mailto:${r.email}`} className="font-body text-sm font-medium text-forest-600">{r.email}</a>
                    <div className="font-body text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="mt-3 inline-block rounded-full bg-forest-50 px-3 py-1 font-body text-xs font-medium text-forest">
                  {r.project}
                </div>
                <p className="mt-3 font-body text-sm leading-relaxed text-forest">{r.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
